import React, { useEffect, useRef, useState } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular } from '../../lib/font';
import Modal from '../modal/Modal';
import { getCoachNameListAPI } from '../../api/center';
import ColorSquareButton from '../Button/ColorSquareButton';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import AttendanceToggle from './AttendanceToggle';
import GroupRuleFields, { RuleFieldsValue, diffRuleFields, initRuleFields, rememberSavedRules, reseedRuleFields, withSavedRules } from './GroupRuleFields';
import { useReservationRules } from '@/features/reservationRules/hooks';
import dayjs from 'dayjs';
import { useGroupSessionDetail, useUpdateGroupSession, useDeleteGroupSession } from '@/features/groupSession/hooks';
import { DeleteSessionScope } from '@/features/groupSession/types';
import { useUpdateBookingStatus, useCancelReservation, useReserveSession, useForcePromoteWaitlist } from '@/features/groupBooking/hooks';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { useMemberTickets, useTickets, useCreateMemberTicket, useCenterMemberTickets, useUpdateMemberTicket } from '@/features/groupTicket/hooks';
import { Booking, BookingStatus } from '@/features/groupBooking/types';
import { Ticket } from '@/features/groupTicket/types';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';
import { MembershipPauseState } from '@/features/membershipPause/types';
import { isPassInUse } from '@/features/membershipPause/status';
import useMembershipPauseCreateGate from '../MembershipPause/useMembershipPauseCreateGate';

const CLASS_TYPE_LABEL: Record<string, string> = {
    GROUP: '그룹', PRIVATE_1ON2: '1:2', PRIVATE_1ON3: '1:3',
};

const formatBookingAddedAt = (bookedAt?: string) =>
    bookedAt && dayjs(bookedAt).isValid() ? dayjs(bookedAt).format('M월 D일 HH시 mm분') : '';

const normalizeId = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'object') return value._id || '';
    return String(value);
};

/** 회원 추가 시 정지 확인 창에 필요한 수강권 정보 */
type PauseCheckTicket = {
    memberName?: string;
    name?: string;
    pause?: MembershipPauseState | null;
    status?: string;
    expiryDate?: string | null;
    isShared?: boolean;
    ownerName?: string;
};

const toPauseCheckTicket = (
    memberName: string,
    t: { name: string; pause?: MembershipPauseState | null; status?: string; expiryDate?: string | null; isShared?: boolean; ownerName?: string },
): PauseCheckTicket => ({
    memberName,
    name: t.name,
    pause: t.pause ?? null,
    status: t.status,
    expiryDate: t.expiryDate ?? null,
    isShared: t.isShared === true,
    ownerName: t.ownerName || '',
});

const isTicketCompatibleWithClassType = (ticket: Ticket, classType?: string): boolean => {
    if (!classType) return false;

    const allowedClassTypes = Array.isArray(ticket.allowedClassTypes) ? ticket.allowedClassTypes : [];
    if (allowedClassTypes.length > 0) return allowedClassTypes.includes(classType);

    return ticket.classType === classType;
};

const GroupSessionDetailPopover = ({
    sessionId,
    onClose,
    createAlert,
}: {
    sessionId: string;
    onClose: () => void;
    createAlert: any;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const centerId = authObject?.centerId;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 560;
    const modalWidth = Math.min(560, Math.max(320, viewportWidth - 24));
    const overlayDialogWidth = Math.min(480, Math.max(280, viewportWidth - 32));
    const compactModal = modalWidth < 520;

    const { data: detail, isError: isDetailError, dataUpdatedAt: detailUpdatedAt, isFetching: isDetailFetching } = useGroupSessionDetail({ centerId, sessionId });
    const { data: centerRules } = useReservationRules({ centerId });
    const updateStatusMutation = useUpdateBookingStatus();
    const cancelMutation = useCancelReservation();
    const reserveMutation = useReserveSession();
    const forcePromoteMutation = useForcePromoteWaitlist();
    const updateSessionMutation = useUpdateGroupSession();
    const settingsLocked = isDetailFetching || isDetailError || updateSessionMutation.isLoading;
    const deleteSessionMutation = useDeleteGroupSession();
    const queryClient = useQueryClient();
    /*
     * 스케줄 동작 잠금(설계 §6). 이 팝오버는 스케줄·회원 상세·구 그룹 화면 세 곳에 붙는다(구 화면 적용은 의도).
     *  수정: 설정·사용 가능 수강권 저장, 출석, 예약 취소, 회원 추가, 대기 승격, 회원별 회차 수정, 발급하고 예약
     *  삭제: 폐강(이 수업만·이후 모두)
     * 누를 때 묻고(run), 실제로 보내기 직전에 다시 본다(30분 만료 구멍).
     */
    const editLock = useScheduleActionLock('schedule.edit');
    const deleteLock = useScheduleActionLock('schedule.delete');
    const updateMemberTicketMutation = useUpdateMemberTicket();

    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [sessionEditTarget, setSessionEditTarget] = useState<{
        memberTicketId: string; currentSession: number; totalSession: number; minSession: number; memberName: string; sessionIndex: number;
    } | null>(null);
    const [sessionEditValue, setSessionEditValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [cancelTarget, setCancelTarget] = useState<{ memberId: string; name: string } | null>(null);
    const [penaltyExempt, setPenaltyExempt] = useState(true);
    const [selectedMemberForTicket, setSelectedMemberForTicket] = useState<string | null>(null);
    const [showIssueTicket, setShowIssueTicket] = useState<{ memberId: string; memberName: string } | null>(null);
    const [issueTicketId, setIssueTicketId] = useState('');
    const [showSessionAction, setShowSessionAction] = useState<'cancel' | null>(null);
    const [deleteScope, setDeleteScope] = useState<DeleteSessionScope>('single');
    const [showEditSettings, setShowEditSettings] = useState(false);
    const [editCoachId, setEditCoachId] = useState('');
    const [editMaxCapacity, setEditMaxCapacity] = useState(0);
    const [editMinCapacity, setEditMinCapacity] = useState(0);
    const [editWaitlistLimit, setEditWaitlistLimit] = useState(0);
    const [editRoomId, setEditRoomId] = useState('');
    const [editMemo, setEditMemo] = useState('');
    const [editRules, setEditRules] = useState<RuleFieldsValue>(() => initRuleFields({}));
    // 센터 설정이 설정 창을 연 뒤에 오면, 임시 기본값으로 채웠던 칸을 센터 값으로 다시 채운다
    useEffect(() => {
        if (centerRules) setEditRules((prev) => reseedRuleFields(prev, centerRules));
    }, [centerRules]);
    const [showAllowedTicketEdit, setShowAllowedTicketEdit] = useState(false);
    const [editAllowedTicketIds, setEditAllowedTicketIds] = useState<string[]>([]);
    const [editVisibleTicketIds, setEditVisibleTicketIds] = useState<string[]>([]);
    const [ticketSearch, setTicketSearch] = useState('');

    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const session = detail?.session;
    const reservations = detail?.reservations ?? [];
    const confirmed = reservations.filter((b) => b.status !== 'WAITLISTED' && b.status !== 'CANCELLED');
    const waitlisted = reservations.filter((b) => b.status === 'WAITLISTED');

    // 회원 검색
    const { data: memberSearchData } = usePublicMemberList({
        centerId, search: searchQuery || undefined, memberType: 'all', limit: 20,
        enabled: showAddMemberModal && searchQuery.length >= 1,
    });
    const searchResults = memberSearchData?.results ?? [];
    const bookedMemberIds = new Set(reservations.filter((b) => b.status !== 'CANCELLED').map((b) => b.memberId));
    const availableMembers = searchResults.filter((m) => m.memberType !== 'public' && !bookedMemberIds.has(m.memberId));

    const { data: selectedMemberTickets = [] } = useMemberTickets({
        centerId, memberId: selectedMemberForTicket || '', status: 'ACTIVE',
        enabled: !!selectedMemberForTicket,
    });
    const allowedTicketIds = (session?.allowedTicketIds ?? []).map(normalizeId).filter(Boolean);
    const matchingTickets = selectedMemberTickets.filter((mt: any) => {
        const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
        return allowedTicketIds.includes(tid) && mt.remainingCount > 0;
    });

    const { data: allMemberData } = usePublicMemberList({ centerId, memberType: 'all', limit: 200 });
    const memberNameMap = new Map<string, string>();
    const memberTypeMap = new Map<string, string>();
    (allMemberData?.results ?? []).forEach((m: any) => { memberNameMap.set(m.memberId || m._id, m.name); });
    (allMemberData?.results ?? []).forEach((m: any) => { memberTypeMap.set(m.memberId || m._id, m.memberType); });

    const { data: coachNameListResponse } = useQuery(
        ['groupSessionCoachNameList', centerId],
        () => getCoachNameListAPI(centerId, authorization),
        { enabled: !!centerId && !!authorization, staleTime: 5 * 60 * 1000 },
    );
    const coachList = (() => {
        const map = new Map<string, string>();
        (coachNameListResponse?.data?.coachNameList ?? []).forEach((coach: any) => {
            if (coach.coachId && !map.has(coach.coachId)) {
                map.set(coach.coachId, coach.name || coach.coachDisplayAlias || coach.coachName || coach.coachId);
            }
        });
        return Array.from(map.entries()).map(([code, value]) => ({ code, value }));
    })();

    // includeShared: 공유만 받은 회원은 소유 티켓이 없어 이 목록에 안 잡히고,
    // 그 결과 예약 가능한 회원인데도 직원이 수강권을 새로 발급하게 된다.
    const { data: centerTicketData } = useCenterMemberTickets({ centerId, params: { status: 'ACTIVE', limit: '1000', activeMembersOnly: 'true', includeShared: 'true' }, enabled: showAddMemberModal && !searchQuery });
    const eligibleMembers = (() => {
        if (!centerTicketData?.results || !allowedTicketIds.length) return [];
        const memberMap = new Map<string, { memberId: string; memberName: string; tickets: { mtId: string; name: string; remaining: number; isShared?: boolean; ownerName?: string; pause?: MembershipPauseState | null; status?: string; expiryDate?: string | null }[] }>();
        for (const mt of centerTicketData.results as any[]) {
            const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
            if (
                allowedTicketIds.includes(tid) &&
                mt.remainingCount > 0 &&
                !bookedMemberIds.has(mt.memberId) &&
                memberTypeMap.get(mt.memberId) !== 'public'
            ) {
                if (!memberMap.has(mt.memberId)) {
                    memberMap.set(mt.memberId, {
                        memberId: mt.memberId,
                        memberName: memberNameMap.get(mt.memberId) || mt.memberName || '(이름 없음)',
                        tickets: [],
                    });
                }
                memberMap.get(mt.memberId)!.tickets.push({
                    mtId: mt._id,
                    name: mt.ticketName || (typeof mt.ticketId === 'object' ? mt.ticketId?.name : '') || '수강권',
                    remaining: mt.remainingCount,
                    isShared: mt.isShared === true,
                    ownerName: mt.ownerName || '',
                    // 수강권 일시정지 — 생성 확인 창 판정용(목록 행에 원본 pause 가 펼쳐져 온다)
                    pause: mt.pause ?? null,
                    status: mt.status,
                    expiryDate: mt.expiryDate ?? null,
                });
            }
        }
        return Array.from(memberMap.values());
    })();

    const { data: ticketProducts = [], isLoading: ticketProductsLoading } = useTickets({
        centerId,
        enabled: !!showIssueTicket || showAllowedTicketEdit,
    });
    const createMemberTicketMutation = useCreateMemberTicket();
    const applicableTickets = (ticketProducts as Ticket[]).filter((t) => allowedTicketIds.includes(t._id));
    const compatibleTicketProducts = (ticketProducts as Ticket[]).filter((ticket) => {
        const isCompatible = isTicketCompatibleWithClassType(ticket, session?.classType);
        return isCompatible || editVisibleTicketIds.includes(ticket._id);
    });
    const filteredTicketProducts = compatibleTicketProducts
        .filter((ticket) => {
            if (!ticketSearch.trim()) return true;
            return ticket.name.toLowerCase().includes(ticketSearch.trim().toLowerCase());
        })
        .sort((a, b) => {
            const aSelected = editAllowedTicketIds.includes(a._id);
            const bSelected = editAllowedTicketIds.includes(b._id);
            if (aSelected !== bSelected) return aSelected ? -1 : 1;
            return a.name.localeCompare(b.name, 'ko');
        });

    const openAllowedTicketEdit = () => editLock.run(() => {
        setEditAllowedTicketIds(allowedTicketIds);
        setEditVisibleTicketIds(allowedTicketIds);
        setTicketSearch('');
        setShowAllowedTicketEdit(true);
    });

    const toggleAllowedTicket = (ticketId: string) => {
        setEditAllowedTicketIds((prev) =>
            prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
        );
    };

    const handleSaveAllowedTickets = async () => {
        if (editAllowedTicketIds.length === 0) {
            showToast('사용 가능 수강권을 1개 이상 선택해주세요.', 'error');
            return;
        }
        if (editLock.requiresCheck() && !(await editLock.ensure())) return;

        try {
            await updateSessionMutation.mutateAsync({
                centerId,
                sessionId,
                body: { allowedTicketIds: editAllowedTicketIds },
            });
            queryClient.invalidateQueries({ queryKey: ['groupTicket'] });
            showToast('사용 가능 수강권이 저장되었습니다.');
            setShowAllowedTicketEdit(false);
        } catch (err: any) {
            showToast(err?.serverMessage || err?.message || '사용 가능 수강권 저장 실패', 'error');
        }
    };

    const handleStatusChange = (bookingId: string, newStatus: BookingStatus) => {
        editLock.run(() =>
            updateStatusMutation.mutate(
                { centerId, bookingId, body: { status: newStatus }, sessionId },
                { onError: (err: any) => showToast(err?.serverMessage || err?.message || '상태 변경 실패', 'error') }
            )
        );
    };

    const handleCancel = async () => {
        if (!cancelTarget) return;
        // 확인 창을 열어 둔 사이 인증이 만료됐을 수 있다 — 보내기 직전에 다시 본다
        if (editLock.requiresCheck() && !(await editLock.ensure())) return;
        cancelMutation.mutate(
            { centerId, sessionId, memberId: cancelTarget.memberId, params: { cancelledBy: 'admin', penaltyExempt: penaltyExempt ? 'true' : 'false' } },
            {
                onSuccess: (result) => {
                    const promoted = result?.data?.promoted;
                    let msg = `${cancelTarget.name}님의 예약이 취소되었습니다.`;
                    if (promoted) msg += ` ${promoted.memberName}님이 대기에서 확정되었습니다.`;
                    showToast(msg);
                    setCancelTarget(null); setPenaltyExempt(true);
                },
                onError: (err: any) => { showToast(err?.serverMessage || err?.message || '예약 취소 실패', 'error'); setCancelTarget(null); setPenaltyExempt(true); },
            }
        );
    };

    const pauseGate = useMembershipPauseCreateGate({ createAlert });

    const handleAddMember = async (memberId: string, memberTicketId: string, ticket?: PauseCheckTicket) => {
        // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
        if (editLock.requiresCheck() && !(await editLock.ensure())) return;
        // 정지 기간 안 수업이면 확인 창(G2). 「발급하고 예약」은 새 수강권이라 ticket 이 없다 → 창 없음.
        if (ticket && session?.startTime) {
            const proceed = await pauseGate.confirmBeforeCreate([
                {
                    ref: { kind: 'group', centerId, memberTicketId },
                    pause: ticket.pause,
                    inUse: isPassInUse(ticket),
                    memberName: ticket.memberName || '회원',
                    passName: ticket.name,
                    startTimes: [session.startTime],
                    expiry: ticket.expiryDate ?? null,
                    isShared: ticket.isShared === true,
                    sharedOwnerName: ticket.isShared ? ticket.ownerName || null : null,
                },
            ]);
            if (!proceed) return;
        }
        reserveMutation.mutate(
            { centerId, sessionId, body: { memberId, memberTicketId, bookedBy: 'admin' } },
            {
                onSuccess: (result) => {
                    const status = result?.data?.booking?.status;
                    showToast(status === 'WAITLISTED' ? '대기 등록되었습니다.' : '예약이 완료되었습니다.');
                    setSearchQuery(''); setSelectedMemberForTicket(null);
                },
                onError: (err: any) => {
                    const message = err?.serverMessage || err?.message || '예약 실패';
                    const decorated = pauseGate.decorateFailureMessage(message);
                    // 「풀고 등록」 뒤 실패면 정지가 풀린 사실을 알림으로 남긴다(토스트는 금방 사라진다).
                    if (decorated !== message) createAlert('수업 등록 실패', decorated, true);
                    else showToast(message, 'error');
                },
            }
        );
    };

    const handleIssueAndReserve = async () => {
        if (!showIssueTicket || !issueTicketId) return;
        // 발급(돈이 걸린 첫 쓰기) **전에** 예약(수정) 잠금을 본다 — 발급만 되고 예약이 막히는 일을 만들지 않는다(설계 §6-6)
        if (editLock.requiresCheck() && !(await editLock.ensure())) return;
        try {
            const result = await createMemberTicketMutation.mutateAsync({
                centerId, memberId: showIssueTicket.memberId,
                body: { ticketId: issueTicketId, startDate: dayjs().format('YYYY-MM-DD'), price: 0, paymentMethod: 'cash', paymentDate: dayjs().format('YYYY-MM-DD') },
            });
            const mtId = result?.data?.memberTicket?._id;
            if (!mtId) {
                showToast('수강권이 발급되지 않았습니다. 다시 시도해주세요.', 'error');
                return;
            }
            handleAddMember(showIssueTicket.memberId, mtId);
            setShowIssueTicket(null); setIssueTicketId('');
        } catch (err: any) {
            showToast(err?.serverMessage || err?.message || '수강권 발급 실패', 'error');
        }
    };

    if (!session) return (
        <Modal isCloseButton setIsVisible={onClose} onClose={onClose} whiteViewWidth={modalWidth}>
            <Col style={{ padding: '60px 28px', alignItems: 'center', gap: 12 }}>
                {isDetailError ? (
                    <>
                        <span style={{ fontSize: 16, color: '#DC2626' }}>수업 정보를 불러올 수 없습니다</span>
                        <button onClick={onClose} style={{ fontSize: 14, color: '#3B82F6', background: 'none', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 20px', cursor: 'pointer' }}>닫기</button>
                    </>
                ) : (
                    <span style={{ fontSize: 16, color: '#9CA3AF' }}>수업 정보를 불러오는 중...</span>
                )}
            </Col>
        </Modal>
    );

    const capacityFill = session.maxCapacity > 0 ? Math.round((session.currentCount / session.maxCapacity) * 100) : 0;

    return (
        <Modal isCloseButton setIsVisible={onClose} onClose={onClose} whiteViewWidth={modalWidth}>
            <Col style={{ padding: compactModal ? '24px 16px' : '28px 32px', overflow: 'auto', maxHeight: '85vh', position: 'relative' }}>
                {/* 토스트 */}
                {toast && (
                    <div style={{
                        position: 'sticky', top: 0, zIndex: 10, marginBottom: 12,
                        padding: '10px 16px', borderRadius: 8,
                        backgroundColor: toast.type === 'error' ? '#FEE2E2' : '#D1FAE5',
                        color: toast.type === 'error' ? '#DC2626' : '#065F46',
                        fontSize: 14, fontWeight: 600, textAlign: 'center',
                    }}>
                        {toast.message}
                    </div>
                )}

                {/* ── 헤더: 수업명 + 날짜 ── */}
                <Col style={{ gap: 6, marginBottom: 20 }}>
                    <Row style={{ alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{session.title}</span>
                        <span style={{
                            fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                            backgroundColor: session.classType === 'GROUP' ? '#ECFDF5' : '#EFF6FF',
                            color: session.classType === 'GROUP' ? '#065F46' : '#1E40AF',
                        }}>
                            {CLASS_TYPE_LABEL[session.classType] || session.classType}
                        </span>
                    </Row>
                    <span style={{ fontSize: 15, color: '#374151' }}>
                        {dayjs(session.startTime).format('YYYY.MM.DD (ddd)')}
                    </span>
                    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
                            {dayjs(session.startTime).format('HH:mm')} ~ {dayjs(session.endTime).format('HH:mm')}
                        </span>
                        <button disabled={settingsLocked} title={isDetailError ? '수업 정보를 다시 불러오지 못했어요. 창을 닫고 다시 열어 주세요' : undefined} onClick={() => {
                            // 상세를 다시 받는 중·받기에 실패한 뒤에 열면 옛 값이 편집 기준이 된다(예: 수업 전체 규칙을 막 바꾼 직후).
                            // 저장 중에 다시 열면 앞 저장이 끝나며 새로 연 창을 닫는다 — 끝난 뒤에 연다
                            if (settingsLocked) return;
                            editLock.run(() => {
                            setEditCoachId(session.coachId || '');
                            setEditMaxCapacity(session.maxCapacity);
                            setEditMinCapacity(session.minCapacity ?? 0);
                            setEditWaitlistLimit(session.waitlistLimit ?? 0);
                            setEditRoomId(session.roomId || '');
                            setEditMemo(session.memo || '');
                            setEditRules(initRuleFields(withSavedRules(`session:${sessionId}`, session, detailUpdatedAt), centerRules));
                            setShowEditSettings(true);
                            });
                        }} style={{ fontSize: 13, color: '#3B82F6', background: 'none', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 14px', cursor: settingsLocked ? 'wait' : 'pointer', fontWeight: 600, opacity: settingsLocked ? 0.5 : 1 }}>
                            설정
                        </button>
                    </Row>
                </Col>

                {/* ── 핵심 정보 카드 ── */}
                <Row style={{ gap: 10, marginBottom: 20 }}>
                    {session.coachName && (
                        <InfoCard label="강사" value={session.coachName} />
                    )}
                    {session.category && (
                        <InfoCard label="카테고리" value={session.category} />
                    )}
                    {session.roomId && (
                        <InfoCard label="장소" value={session.roomId} />
                    )}
                </Row>

                {/* ── 예약 현황 바 ── */}
                <Col style={{ padding: 16, backgroundColor: '#F8FAFC', borderRadius: 12, marginBottom: 20, gap: 10 }}>
                    <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>예약 현황</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#2563EB' }}>
                            {session.currentCount} / {session.maxCapacity}명
                        </span>
                    </Row>
                    <div style={{ width: '100%', height: 8, backgroundColor: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                            width: `${Math.min(capacityFill, 100)}%`, height: '100%', borderRadius: 4,
                            backgroundColor: capacityFill >= 100 ? '#EF4444' : capacityFill >= 70 ? '#F59E0B' : '#3B82F6',
                            transition: 'width 0.3s',
                        }} />
                    </div>
                    <Row style={{ gap: 16, fontSize: 13, color: '#6B7280' }}>
                        {session.minCapacity > 0 && <span>최소 {session.minCapacity}명</span>}
                        {session.waitlistLimit > 0 && <span>대기 {waitlisted.length}/{session.waitlistLimit}명</span>}
                    </Row>
                </Col>

                {/* ── 수강권 ── */}
                {/* ── 메모 ── */}
                {session.memo && (
                    <Col style={{ marginBottom: 20, padding: '12px 16px', backgroundColor: '#FFFBEB', borderRadius: 10, border: '1px solid #FDE68A' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#92400E', marginBottom: 4 }}>메모</span>
                        <span style={{ fontSize: 14, color: '#78350F', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{session.memo}</span>
                    </Col>
                )}

                <Col style={{ marginBottom: 20, gap: 8 }}>
                    <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>사용 가능 수강권</span>
                        <button
                            type="button"
                            onClick={openAllowedTicketEdit}
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#047857',
                                backgroundColor: '#ECFDF5',
                                border: '1px solid #A7F3D0',
                                borderRadius: 7,
                                padding: '5px 10px',
                                cursor: 'pointer',
                            }}
                        >
                            수정
                        </button>
                    </Row>
                    <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                        {session.allowedTicketNames && session.allowedTicketNames.length > 0 ? (
                            session.allowedTicketNames.map((name: string, i: number) => (
                                <span key={i} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, backgroundColor: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', fontWeight: 500 }}>{name}</span>
                            ))
                        ) : (
                            <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, backgroundColor: '#F8FAFC', color: '#64748B', border: '1px solid #CBD5E1', fontWeight: 600 }}>
                                설정된 수강권 없음
                            </span>
                        )}
                    </Row>
                </Col>

                {/* ── 구분선 ── */}
                <div style={{ height: 1, backgroundColor: '#E5E7EB', marginBottom: 20 }} />

                {/* ── 예약자 목록 ── */}
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>예약자 ({confirmed.length}명)</span>
                    <button onClick={() => editLock.run(() => { setShowAddMemberModal(true); setSearchQuery(''); setSelectedMemberForTicket(null); setShowIssueTicket(null); })}
                        style={{ fontSize: 13, fontWeight: 600, color: '#3B82F6', background: 'none', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
                        + 회원 추가
                    </button>
                </Row>

                {confirmed.length === 0 && (
                    <Col style={{ padding: '24px 0', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, color: '#9CA3AF' }}>예약자가 없습니다</span>
                    </Col>
                )}
                <Col style={{ gap: 6 }}>
                    {confirmed.map((b) => {
                        const bookedByLabel = b.bookedBy === 'admin' ? '관리자' : b.bookedBy === 'coach' ? '강사' : '본인';
                        const ticketName = b.ticketName || (typeof b.memberTicketId === 'object' ? (b.memberTicketId as any)?.name : '') || '수강권';
                        const total = b.totalCount ?? ((b.usedCount ?? 0) + (b.remainingCount ?? 0));
                        const sessionIdx = b.sessionIndex ?? 0;
                        const currentSession = b.currentSession ?? (Math.max(b.startSessionValue ?? 0, 1) + sessionIdx);
                        const totalSession = total;
                        const minSession = sessionIdx + 1;
                        const mtId = b.memberTicketObjId || (typeof b.memberTicketId === 'string' ? b.memberTicketId : (b.memberTicketId as any)?._id || '');
                        const hasSessionInfo = total > 0;
                        return (
                            <Row key={b._id} style={{ padding: '14px 16px', borderRadius: 10, border: '1px solid #F3F4F6', backgroundColor: '#FAFAFA', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Col style={{ gap: 4 }}>
                                    <Row style={{ alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{b.memberName || '(이름 없음)'}</span>
                                        {hasSessionInfo && (
                                            <button
                                                onClick={() => editLock.run(() => {
                                                    setSessionEditTarget({ memberTicketId: mtId, currentSession, totalSession, minSession, memberName: b.memberName || '', sessionIndex: sessionIdx });
                                                    setSessionEditValue(currentSession);
                                                })}
                                                style={{
                                                    fontSize: 13, fontWeight: 700, color: '#2563EB', backgroundColor: '#EFF6FF',
                                                    border: '1px solid #BFDBFE', borderRadius: 6, padding: '2px 10px', cursor: 'pointer',
                                                }}
                                            >
                                                {currentSession}/{totalSession}회
                                            </button>
                                        )}
                                    </Row>
                                    <Row style={{ gap: 8, fontSize: 13, color: '#9CA3AF' }}>
                                        <span>{ticketName}</span>
                                        <span>·</span>
                                        <span>{bookedByLabel} · {formatBookingAddedAt(b.bookedAt)}</span>
                                    </Row>
                                </Col>
                                <Row style={{ alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    <AttendanceToggle status={b.status as any} onChange={(s) => handleStatusChange(b._id, s)} />
                                    <button onClick={() => editLock.run(() => setCancelTarget({ memberId: b.memberId, name: b.memberName || '' }))}
                                        style={{ fontSize: 12, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                                        취소
                                    </button>
                                </Row>
                            </Row>
                        );
                    })}
                </Col>

                {/* ── 대기자 ── */}
                {waitlisted.length > 0 && (
                    <Col style={{ marginTop: 20 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#F59E0B', marginBottom: 10 }}>대기 ({waitlisted.length}명)</span>
                        <Col style={{ gap: 4 }}>
                            {waitlisted.map((b, idx) => {
                                const bookedByLabel = b.bookedBy === 'admin' ? '관리자' : b.bookedBy === 'coach' ? '강사' : '본인';
                                return (
                                    <Row key={b._id} style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, border: '1px solid #FEF3C7', backgroundColor: '#FFFBEB' }}>
                                        <Col>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{idx + 1}. {b.memberName || '(이름 없음)'}</span>
                                            <span style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{bookedByLabel} · {formatBookingAddedAt(b.bookedAt)}</span>
                                        </Col>
                                        <Row style={{ gap: 6, flexShrink: 0 }}>
                                            <button onClick={() => editLock.run(() => forcePromoteMutation.mutate(
                                                { centerId, sessionId, memberId: b.memberId },
                                                { onSuccess: () => showToast(`${b.memberName || '회원'}님이 예약 확정되었습니다.`), onError: (e: any) => showToast(e?.message || '승격 실패', 'error') }
                                            ))} disabled={forcePromoteMutation.isLoading}
                                                style={{ fontSize: 12, color: '#3B82F6', background: 'none', border: '1px solid #BFDBFE', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                                                승격
                                            </button>
                                            <button onClick={() => editLock.run(() => setCancelTarget({ memberId: b.memberId, name: b.memberName || '' }))}
                                                style={{ fontSize: 12, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}>취소</button>
                                        </Row>
                                    </Row>
                                );
                            })}
                        </Col>
                    </Col>
                )}

                {/* ── 폐강 버튼 ── */}
                <Row style={{ marginTop: 24, justifyContent: 'flex-end' }}>
                    <button onClick={() => deleteLock.run(() => setShowSessionAction('cancel'))}
                        style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', cursor: 'pointer' }}>
                        폐강
                    </button>
                </Row>
            </Col>

            {/* ═══ 회차 수정 모달 ═══ */}
            {sessionEditTarget && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 16, padding: compactModal ? 20 : 28, width: Math.min(400, Math.max(280, viewportWidth - 32)), boxShadow: '0 8px 32px rgba(0,0,0,0.15)', alignItems: 'center', gap: 16 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>수강권 회차 수정</span>
                        <span style={{ fontSize: 14, color: '#6B7280' }}>{sessionEditTarget.memberName}님의 회차를 수정합니다</span>
                        <span style={{ fontSize: 13, color: '#9CA3AF' }}>{sessionEditTarget.minSession}회 이상으로 작성해주세요!</span>
                        <Row style={{ alignItems: 'center', gap: 12 }}>
                            <input
                                type="text"
                                value={sessionEditValue === 0 ? '' : sessionEditValue}
                                onChange={(e) => {
                                    let v = e.target.value.replace(/\D/g, '');
                                    let num = v === '' ? 0 : parseInt(v);
                                    if (num > sessionEditTarget.totalSession) num = sessionEditTarget.totalSession;
                                    setSessionEditValue(num);
                                }}
                                style={{ width: 80, height: 44, textAlign: 'center', fontSize: 18, fontWeight: 700, border: '1px solid #D1D5DB', borderRadius: 10, outline: 'none' }}
                                autoFocus
                            />
                            <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>/ {sessionEditTarget.totalSession}회</span>
                        </Row>
                        <Row style={{ gap: 10, marginTop: 8 }}>
                            <button
                                onClick={() => setSessionEditTarget(null)}
                                style={{ height: 44, minWidth: 100, borderRadius: 10, fontSize: 14, fontWeight: 600, backgroundColor: '#FFFFFF', color: '#4B5563', border: '1px solid #D1D5DB', cursor: 'pointer' }}
                            >
                                취소
                            </button>
                            <button
                                onClick={() => {
                                    if (sessionEditValue < sessionEditTarget.minSession) {
                                        showToast(`${sessionEditTarget.minSession}회 이상의 값을 입력해주세요!`, 'error');
                                        return;
                                    }
                                    const newSsv = sessionEditValue - sessionEditTarget.sessionIndex;
                                    // 회차 수정은 수강권 수정과 같은 API — API 가 아니라 이 버튼에서만 스케줄 수정 잠금을 본다
                                    editLock.run(() => updateMemberTicketMutation.mutate(
                                        { centerId, memberTicketId: sessionEditTarget.memberTicketId, body: { startSessionValue: newSsv } },
                                        {
                                            onSuccess: () => {
                                                showToast(`${sessionEditTarget.memberName}님의 회차가 ${sessionEditValue}회차로 변경되었습니다.`);
                                                setSessionEditTarget(null);
                                                queryClient.invalidateQueries({ queryKey: ['groupSession'] });
                                                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                                                queryClient.invalidateQueries({ queryKey: ['groupTicket'] });
                                            },
                                            onError: (err: any) => showToast(err?.serverMessage || err?.message || '회차 수정 실패', 'error'),
                                        }
                                    ));
                                }}
                                disabled={updateMemberTicketMutation.isLoading}
                                style={{ height: 44, minWidth: 120, borderRadius: 10, fontSize: 14, fontWeight: 700, backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer' }}
                            >
                                수정 완료
                            </button>
                        </Row>
                    </Col>
                </div>
            )}

            {/* ═══ 회원 추가 모달 ═══ */}
            {showAddMemberModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 16, padding: compactModal ? 20 : 28, width: overlayDialogWidth, maxHeight: '75vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', position: 'relative' }}>
                        <button onClick={() => { setShowAddMemberModal(false); setSearchQuery(''); setShowIssueTicket(null); }}
                            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 20, color: '#9CA3AF', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>회원 추가</span>
                        <span style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 16 }}>수강권을 보유한 회원을 바로 추가할 수 있습니다</span>

                        {/* 수강권 발급 플로우 */}
                        {showIssueTicket ? (
                            <Col style={{ gap: 12 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>{showIssueTicket.memberName}님에게 수강권 발급</span>
                                {applicableTickets.length === 0 ? (
                                    <span style={{ fontSize: 14, color: '#EF4444' }}>이 수업에 사용 가능한 수강권 상품이 없습니다</span>
                                ) : (
                                    <Col style={{ gap: 8 }}>
                                        {applicableTickets.map((t) => {
                                            const sel = issueTicketId === t._id;
                                            return (
                                                <button key={t._id} onClick={() => setIssueTicketId(t._id)} style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                                                    border: sel ? '2px solid #3B82F6' : '1px solid #E5E7EB', backgroundColor: sel ? '#EFF6FF' : 'white',
                                                }}>
                                                    <span style={{ fontSize: 14, fontWeight: 600, color: sel ? '#1E40AF' : '#374151' }}>{t.name}</span>
                                                    <span style={{ fontSize: 13, color: sel ? '#1E40AF' : '#9CA3AF' }}>{t.totalCount}회 · {t.price.toLocaleString()}원</span>
                                                </button>
                                            );
                                        })}
                                        <Row style={{ gap: 8, marginTop: 4 }}>
                                            <button onClick={() => { setShowIssueTicket(null); setIssueTicketId(''); }}
                                                style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 14, backgroundColor: 'white', color: '#6B7280', border: '1px solid #D1D5DB', cursor: 'pointer' }}>
                                                돌아가기
                                            </button>
                                            <button onClick={handleIssueAndReserve} disabled={!issueTicketId}
                                                style={{ flex: 2, padding: '10px', borderRadius: 8, fontSize: 14, fontWeight: 600, backgroundColor: issueTicketId ? '#3B82F6' : '#D1D5DB', color: 'white', border: 'none', cursor: issueTicketId ? 'pointer' : 'default' }}>
                                                발급 + 예약
                                            </button>
                                        </Row>
                                    </Col>
                                )}
                            </Col>
                        ) : (
                            <Col style={{ gap: 12 }}>
                                <input placeholder="회원 이름 또는 전화번호 검색" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus
                                    style={{ border: '1px solid #D1D5DB', borderRadius: 10, padding: '12px 16px', fontSize: 15, width: '100%', outline: 'none' }} />

                                {searchQuery ? (
                                    /* 검색 결과 — 수강권 정보 인라인 표시 */
                                    <Col style={{ gap: 6 }}>
                                        {availableMembers.length === 0 && <span style={{ fontSize: 14, color: '#9CA3AF', padding: 8 }}>검색 결과가 없습니다</span>}
                                        {availableMembers.map((m) => (
                                            <MemberAddCard
                                                key={m.memberId}
                                                memberId={m.memberId}
                                                memberName={m.name}
                                                phoneNumber={m.phoneNumber}
                                                allowedTicketIds={allowedTicketIds}
                                                centerId={centerId}
                                                onAdd={handleAddMember}
                                                onIssue={(memberId, name) => editLock.run(() => setShowIssueTicket({ memberId, memberName: name }))}
                                            />
                                        ))}
                                    </Col>
                                ) : eligibleMembers.length > 0 ? (
                                    /* 수강 가능 회원 — 수강권 1개면 바로 추가 */
                                    <Col style={{ gap: 6 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#10B981' }}>수강 가능 회원 ({eligibleMembers.length}명)</span>
                                        <Col style={{ gap: 6 }}>
                                            {eligibleMembers.map((m) => (
                                                <EligibleMemberCard
                                                    key={m.memberId}
                                                    member={m}
                                                    onAdd={handleAddMember}
                                                />
                                            ))}
                                        </Col>
                                    </Col>
                                ) : (
                                    <span style={{ fontSize: 14, color: '#9CA3AF', textAlign: 'center', padding: '16px 0' }}>회원 이름을 검색해주세요</span>
                                )}
                            </Col>
                        )}
                    </Col>
                </div>
            )}

            {/* ═══ 사용 가능 수강권 수정 모달 ═══ */}
            {showAllowedTicketEdit && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 16, padding: compactModal ? 20 : 28, width: Math.min(500, Math.max(280, viewportWidth - 32)), maxHeight: '78vh', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', gap: 16 }}>
                        <Row style={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <Col style={{ gap: 5 }}>
                                <span style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>사용 가능 수강권 수정</span>
                                <span style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.45 }}>
                                    이 수업에 새로 예약할 때 사용할 수 있는 수강권을 선택합니다.
                                </span>
                            </Col>
                            <button
                                type="button"
                                onClick={() => setShowAllowedTicketEdit(false)}
                                style={{ border: 'none', background: 'transparent', color: '#9CA3AF', fontSize: 24, lineHeight: 1, cursor: 'pointer' }}
                            >
                                ×
                            </button>
                        </Row>

                        <Col style={{ padding: 12, borderRadius: 10, backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#92400E' }}>기존 예약자는 유지됩니다</span>
                            <span style={{ fontSize: 12, lineHeight: 1.45, color: '#92400E' }}>
                                수강권을 제외해도 이미 예약된 회원은 자동 취소되지 않습니다. 회원 추가, 대기 승격, 회원 수강권에서 수업 등록할 때부터 적용됩니다.
                            </span>
                        </Col>

                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <input
                                value={ticketSearch}
                                onChange={(e) => setTicketSearch(e.target.value)}
                                placeholder="수강권명 검색"
                                style={{
                                    flex: 1,
                                    height: 40,
                                    border: '1px solid #D1D5DB',
                                    borderRadius: 8,
                                    padding: '0 12px',
                                    fontSize: 14,
                                    outline: 'none',
                                }}
                            />
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#047857', whiteSpace: 'nowrap' }}>
                                선택 {editAllowedTicketIds.length}개
                            </span>
                        </Row>

                        <Col style={{ gap: 8, overflowY: 'auto', maxHeight: 310, paddingRight: 2 }}>
                            {ticketProductsLoading ? (
                                <span style={{ fontSize: 13, color: '#9CA3AF', padding: '20px 0', textAlign: 'center' }}>
                                    수강권을 불러오는 중...
                                </span>
                            ) : filteredTicketProducts.length === 0 ? (
                                <span style={{ fontSize: 13, color: '#9CA3AF', padding: '20px 0', textAlign: 'center' }}>
                                    표시할 수강권이 없습니다
                                </span>
                            ) : (
                                filteredTicketProducts.map((ticket) => {
                                    const checked = editAllowedTicketIds.includes(ticket._id);
                                    const compatible = isTicketCompatibleWithClassType(ticket, session?.classType);
                                    const statusLabel = ticket.isActive === false
                                        ? '비활성'
                                        : !compatible
                                            ? checked ? '예외 사용' : '제외 예정'
                                            : checked ? '사용' : '미사용';
                                    const statusColor = ticket.isActive === false
                                        ? '#991B1B'
                                        : !compatible
                                            ? '#92400E'
                                            : checked ? '#047857' : '#64748B';
                                    const statusBg = ticket.isActive === false
                                        ? '#FEE2E2'
                                        : !compatible
                                            ? '#FFFBEB'
                                            : checked ? '#D1FAE5' : '#F8FAFC';
                                    return (
                                        <button
                                            type="button"
                                            key={ticket._id}
                                            onClick={() => toggleAllowedTicket(ticket._id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 12,
                                                width: '100%',
                                                padding: '12px 14px',
                                                borderRadius: 10,
                                                border: checked ? '1px solid #34D399' : !compatible ? '1px dashed #F59E0B' : '1px solid #E5E7EB',
                                                backgroundColor: checked ? '#ECFDF5' : !compatible ? '#FFFBEB' : '#FFFFFF',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Row style={{ alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    readOnly
                                                    style={{ width: 16, height: 16, accentColor: '#059669', flexShrink: 0 }}
                                                />
                                                <Col style={{ gap: 3, minWidth: 0 }}>
                                                    <span style={{ fontSize: 14, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {ticket.name}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: '#64748B' }}>
                                                        {CLASS_TYPE_LABEL[ticket.classType || ''] || ticket.classType || '그룹'} · {ticket.totalCount ? `${ticket.totalCount}회` : ticket.periodDays ? `${ticket.periodDays}일` : '조건 없음'}
                                                    </span>
                                                </Col>
                                            </Row>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 800,
                                                    color: statusColor,
                                                    backgroundColor: statusBg,
                                                    borderRadius: 999,
                                                    padding: '3px 8px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {statusLabel}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </Col>

                        <Row style={{ gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                            <button
                                type="button"
                                onClick={() => setShowAllowedTicketEdit(false)}
                                style={{ height: 40, minWidth: 96, borderRadius: 8, border: '1px solid #D1D5DB', backgroundColor: '#FFFFFF', color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveAllowedTickets}
                                disabled={updateSessionMutation.isLoading}
                                style={{
                                    height: 40,
                                    minWidth: 112,
                                    borderRadius: 8,
                                    border: 'none',
                                    backgroundColor: updateSessionMutation.isLoading ? '#A5ABB5' : '#059669',
                                    color: '#FFFFFF',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    cursor: updateSessionMutation.isLoading ? 'default' : 'pointer',
                                }}
                            >
                                저장
                            </button>
                        </Row>
                    </Col>
                </div>
            )}

            {/* ═══ 수업 설정 수정 모달 ═══ */}
            {showEditSettings && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 16, padding: compactModal ? 20 : 28, width: Math.min(440, Math.max(280, viewportWidth - 32)), boxShadow: '0 8px 32px rgba(0,0,0,0.15)', gap: 16 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>수업 설정 수정</span>
                        {/* 저장 중에는 입력을 잠근다 — 저장이 끝나면 창이 닫혀, 그사이 고친 값은 저장되지 않고 사라진다 */}
                        <fieldset disabled={updateSessionMutation.isLoading} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <Col style={{ gap: 6 }}>
                            <span style={{ fontSize: 13, color: '#6B7280' }}>담당 강사</span>
                            <select value={editCoachId} onChange={(e) => setEditCoachId(e.target.value)}
                                style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%' }}>
                                {/* 강사를 비우는 저장은 없다(빈 값은 보내지 않아 원래 강사가 남는다) — 선택지로 두지 않는다 */}
                                {!editCoachId && <option value="" disabled>강사를 선택해 주세요</option>}
                                {editCoachId && !coachList.some((c) => c.code === editCoachId) && (
                                    <option value={editCoachId}>{session.coachName || '현재 강사'}</option>
                                )}
                                {coachList.map((c) => <option key={c.code} value={c.code}>{c.value}</option>)}
                            </select>
                        </Col>
                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>최대 정원</span>
                                <input type="number" value={editMaxCapacity} min={1} onChange={(e) => setEditMaxCapacity(Number(e.target.value))}
                                    style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%' }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>최소 인원</span>
                                <input type="number" value={editMinCapacity} min={0} onChange={(e) => setEditMinCapacity(Number(e.target.value))}
                                    style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%' }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>대기 인원</span>
                                <input type="number" value={editWaitlistLimit} min={0} onChange={(e) => setEditWaitlistLimit(Number(e.target.value))}
                                    style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%' }} />
                            </Col>
                        </Row>
                        <Col style={{ gap: 6 }}>
                            <span style={{ fontSize: 13, color: '#6B7280' }}>장소</span>
                            <input value={editRoomId} onChange={(e) => setEditRoomId(e.target.value)} placeholder="장소"
                                style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%' }} />
                        </Col>
                        <Col style={{ gap: 6 }}>
                            <span style={{ fontSize: 13, color: '#6B7280' }}>메모</span>
                            <textarea value={editMemo} onChange={(e) => setEditMemo(e.target.value)} placeholder="수업 관련 메모 (선택)"
                                rows={2} style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', resize: 'vertical' }} />
                        </Col>
                        {/* 이 회차만의 예약 규칙 — 같은 수업의 다른 날짜는 그대로다 */}
                        <GroupRuleFields
                            value={editRules}
                            centerRules={centerRules}
                            onChange={setEditRules}
                            inputStyle={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}
                        />
                        {Object.keys(diffRuleFields(editRules)).length > 0 && (
                            <span style={{ fontSize: 12, color: '#B45309', lineHeight: 1.6 }}>
                                이 날짜 수업에만 적용되고, 이미 예약한 회원에게도 바로 적용돼요.
                            </span>
                        )}
                        </fieldset>
                        <Row style={{ gap: 8, marginTop: 4 }}>
                            <WhiteSquareButton text="취소" size="small" callBack={() => setShowEditSettings(false)} />
                            <ColorSquareButton text="저장" size="small"
                                callBack={async () => {
                                    if (editLock.requiresCheck() && !(await editLock.ensure())) return;
                                    try {
                                        await updateSessionMutation.mutateAsync({
                                            centerId, sessionId,
                                            body: {
                                                coachId: editCoachId || undefined, maxCapacity: editMaxCapacity, minCapacity: editMinCapacity, waitlistLimit: editWaitlistLimit, roomId: editRoomId || undefined,
                                                // 지운 메모도 저장되게 빈 문자열을 보낸다(전에는 undefined 라 지워지지 않았다)
                                                memo: editMemo.trim(),
                                                ...diffRuleFields(editRules),
                                            },
                                        });
                                        rememberSavedRules(`session:${sessionId}`, diffRuleFields(editRules));
                                        showToast('수업 설정이 저장되었습니다.');
                                        setShowEditSettings(false);
                                    } catch (err: any) {
                                        showToast(err?.serverMessage || err?.message || '저장 실패', 'error');
                                    }
                                }}
                                isDisabled={updateSessionMutation.isLoading}
                            />
                        </Row>
                    </Col>
                </div>
            )}

            {/* ═══ 취소 확인 모달 ═══ */}
            {cancelTarget && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 12, padding: compactModal ? 20 : 28, width: Math.min(400, Math.max(280, viewportWidth - 32)), gap: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                        <span style={{ fontSize: 18, fontWeight: 600, color: '#374151' }}>예약 취소</span>
                        <span style={{ fontSize: 15, color: '#6B7280' }}><b>{cancelTarget.name}</b>님의 예약을 취소하시겠습니까?</span>
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" checked={penaltyExempt} onChange={() => setPenaltyExempt(!penaltyExempt)} />
                            <span style={{ fontSize: 14, color: '#374151' }}>패널티 면제 (수강권 복원)</span>
                        </Row>
                        <Row style={{ gap: 12, justifyContent: 'flex-end' }}>
                            <WhiteSquareButton text="돌아가기" size="small" callBack={() => setCancelTarget(null)} />
                            <ColorSquareButton text="취소 진행" size="small" callBack={handleCancel} isDisabled={cancelMutation.isLoading} />
                        </Row>
                    </Col>
                </div>
            )}

            {/* ═══ 수업 삭제 모달 (scope 선택) ═══ */}
            {showSessionAction === 'cancel' && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
                    <Col style={{ backgroundColor: 'white', borderRadius: 12, padding: compactModal ? 20 : 28, width: Math.min(440, Math.max(280, viewportWidth - 32)), gap: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>⚠ 수업을 삭제하시겠어요?</span>
                        <Col style={{ padding: '12px 14px', backgroundColor: '#F9FAFB', borderRadius: 8, gap: 4 }}>
                            <span style={{ fontSize: 14, color: '#6B7280' }}>
                                {dayjs(session.startTime).format('YYYY-MM-DD (dd) HH:mm')}
                            </span>
                            <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
                                {session.title} ({CLASS_TYPE_LABEL[session.classType] || session.classType})
                            </span>
                        </Col>

                        {session.recurringGroupId ? (
                            <Col style={{ gap: 10 }}>
                                <ScopeOption
                                    label="이 수업만 삭제"
                                    description="해당 세션 하나만 삭제됩니다"
                                    selected={deleteScope === 'single'}
                                    onSelect={() => setDeleteScope('single')}
                                />
                                <ScopeOption
                                    label="이 수업 이후 모두 삭제"
                                    description="같은 묶음의 이후 세션을 모두 삭제합니다"
                                    selected={deleteScope === 'future'}
                                    onSelect={() => setDeleteScope('future')}
                                />
                            </Col>
                        ) : (
                            <span style={{ fontSize: 13, color: '#6B7280' }}>
                                단일 세션입니다. 이 수업만 삭제됩니다.
                            </span>
                        )}

                        <span style={{ fontSize: 13, color: '#92400E', backgroundColor: '#FEF3C7', padding: '8px 12px', borderRadius: 6, border: '1px solid #FDE68A' }}>
                            예약된 회원의 수강권이 복원됩니다. 삭제 안내 푸시가 발송됩니다.
                        </span>

                        <Row style={{ gap: 12, justifyContent: 'flex-end' }}>
                            <WhiteSquareButton text="취소" size="small" callBack={() => { setShowSessionAction(null); setDeleteScope('single'); }} />
                            <ColorSquareButton text="삭제하기" size="small"
                                callBack={async () => {
                                    const scope = session.recurringGroupId ? deleteScope : 'single';
                                    if (deleteLock.requiresCheck() && !(await deleteLock.ensure())) return;
                                    try {
                                        const result = await deleteSessionMutation.mutateAsync({ centerId, sessionId, scope });
                                        const d = result?.data;
                                        const cancelled = d?.cancelledSessions ?? 1;
                                        const refunded = d?.refundedBookings ?? 0;
                                        setShowSessionAction(null);
                                        setDeleteScope('single');
                                        onClose();
                                        // popover unmount 후에도 보이도록 전역 alert 사용
                                        createAlert(
                                            '삭제 완료',
                                            `${cancelled}개 수업이 삭제되었어요.${refunded > 0 ? `\n${refunded}명의 수강권이 복원되었습니다.` : ''}`,
                                            false,
                                        );
                                    } catch (err: any) {
                                        showToast(err?.serverMessage || err?.message || '삭제 실패', 'error');
                                    }
                                }}
                                isDisabled={deleteSessionMutation.isLoading}
                            />
                        </Row>
                    </Col>
                </div>
            )}
            {pauseGate.modal}
            {editLock.gate}
            {deleteLock.gate}
        </Modal>
    );
};

const ScopeOption = ({ label, description, selected, onSelect }: {
    label: string; description: string; selected: boolean; onSelect: () => void;
}) => (
    <Row onClick={onSelect} style={{
        alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
        border: selected ? '2px solid #3B82F6' : '1.5px solid #E5E7EB',
        backgroundColor: selected ? '#EFF6FF' : 'white',
    }}>
        <div style={{
            width: 18, height: 18, borderRadius: '50%',
            border: `2px solid ${selected ? '#3B82F6' : '#D1D5DB'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
            {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3B82F6' }} />}
        </div>
        <Col style={{ gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: selected ? '#1D4ED8' : '#111827' }}>{label}</span>
            <span style={{ fontSize: 12, color: '#6B7280' }}>{description}</span>
        </Col>
    </Row>
);

const InfoCard = ({ label, value }: { label: string; value: string }) => (
    <Col style={{ flex: 1, padding: '10px 14px', backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #F3F4F6' }}>
        <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginTop: 2 }}>{value}</span>
    </Col>
);

/** 수강 가능 회원 카드 — 수강권 1개면 바로 추가, 여러 개면 인라인 선택 */
const EligibleMemberCard = ({ member, onAdd }: {
    member: {
        memberId: string;
        memberName: string;
        tickets: { mtId: string; name: string; remaining: number; isShared?: boolean; ownerName?: string; pause?: MembershipPauseState | null; status?: string; expiryDate?: string | null }[];
    };
    onAdd: (memberId: string, memberTicketId: string, ticket?: PauseCheckTicket) => void;
}) => {
    const [expanded, setExpanded] = useState(false);
    const hasMultiple = member.tickets.length > 1;
    // 공유받은 수강권으로 추가하는 것이라 소유자가 누구인지 보여야, 직원이 잔여 회차가
    // 왜 같은지·누구 회차를 쓰는지 알 수 있다.
    const sharedLabel = (t: { isShared?: boolean; ownerName?: string }) =>
        t.isShared ? ` · 공유(${t.ownerName || '소유자 확인 필요'})` : '';

    return (
        <Col style={{ borderRadius: 10, border: '1px solid #D1FAE5', backgroundColor: 'white', overflow: 'hidden', flexShrink: 0 }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
                <Col style={{ gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{member.memberName}</span>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                        {hasMultiple
                            ? `수강권 ${member.tickets.length}개 보유`
                            : `${member.tickets[0].name} (${member.tickets[0].remaining}회)${sharedLabel(member.tickets[0])}`
                        }
                    </span>
                </Col>
                {hasMultiple ? (
                    <button onClick={() => setExpanded(!expanded)}
                        style={{ fontSize: 13, fontWeight: 600, color: '#10B981', background: 'none', border: '1px solid #A7F3D0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
                        {expanded ? '접기' : '수강권 선택'}
                    </button>
                ) : (
                    <button onClick={() => onAdd(member.memberId, member.tickets[0].mtId, toPauseCheckTicket(member.memberName, member.tickets[0]))}
                        style={{ fontSize: 13, fontWeight: 600, color: 'white', background: '#10B981', border: 'none', borderRadius: 8, padding: '6px 16px', cursor: 'pointer' }}>
                        추가
                    </button>
                )}
            </Row>
            {expanded && (
                <Col style={{ borderTop: '1px solid #ECFDF5', padding: '8px 16px 12px', gap: 4 }}>
                    {member.tickets.map((t) => (
                        <Row key={t.mtId} style={{ alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, backgroundColor: '#F0FDF4' }}>
                            <span style={{ fontSize: 13, color: '#374151' }}>{t.name} ({t.remaining}회){sharedLabel(t)}</span>
                            <button onClick={() => onAdd(member.memberId, t.mtId, toPauseCheckTicket(member.memberName, t))}
                                style={{ fontSize: 12, fontWeight: 600, color: 'white', background: '#10B981', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                                추가
                            </button>
                        </Row>
                    ))}
                </Col>
            )}
        </Col>
    );
};

/** 검색된 회원 카드 — 보유 수강권 자동 조회 + 인라인 표시 */
const MemberAddCard = ({ memberId, memberName, phoneNumber, allowedTicketIds, centerId, onAdd, onIssue }: {
    memberId: string; memberName: string; phoneNumber?: string;
    allowedTicketIds: string[]; centerId: string;
    onAdd: (memberId: string, memberTicketId: string, ticket?: PauseCheckTicket) => void;
    onIssue: (memberId: string, name: string) => void;
}) => {
    const [expanded, setExpanded] = useState(false);
    // 공유만 받은 회원은 본인 소유 수강권이 없어 기본 조회로는 아무것도 안 나온다.
    // 직원 예약은 서버가 이미 허용하므로(bookedBy=admin) 공유 행까지 받아 추가할 수 있게 한다.
    const { data: tickets = [] } = useMemberTickets({
        centerId, memberId, status: 'ACTIVE', enabled: expanded, includeShared: true,
    });
    const matching = (tickets as any[]).filter((mt) => {
        const tid = typeof mt.ticketId === 'object' ? mt.ticketId?._id : mt.ticketId;
        return allowedTicketIds.includes(tid) && mt.remainingCount > 0;
    });

    return (
        <Col style={{ borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: 'white', overflow: 'hidden', flexShrink: 0 }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}>
                <Col style={{ gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{memberName}</span>
                    {phoneNumber && <span style={{ fontSize: 12, color: '#9CA3AF' }}>{formatKoreanPhoneNumber(phoneNumber, phoneNumber)}</span>}
                </Col>
                <span style={{ fontSize: 12, color: '#3B82F6', fontWeight: 600 }}>{expanded ? '접기' : '수강권 확인'}</span>
            </Row>
            {expanded && (
                <Col style={{ borderTop: '1px solid #F3F4F6', padding: '10px 16px 12px', gap: 6 }}>
                    {matching.length > 0 ? (
                        matching.map((mt: any) => (
                            <Row key={mt._id} style={{ alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, backgroundColor: '#F8FAFC' }}>
                                <span style={{ fontSize: 13, color: '#374151' }}>
                                    {mt.ticketName || (typeof mt.ticketId === 'object' ? mt.ticketId?.name : '') || '수강권'} ({mt.remainingCount}회)
                                    {/* 남의 수강권을 공유받아 쓰는 행이다. 소유자를 밝히지 않으면 회차가 왜 줄었는지 알 수 없다. */}
                                    {mt.access?.type === 'SHARED' && (
                                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#008977' }}>
                                            공유 · {mt.access.ownerName || '소유자 확인 필요'}
                                        </span>
                                    )}
                                </span>
                                <button onClick={() =>
                                        onAdd(memberId, mt._id, {
                                            memberName,
                                            name: mt.ticketName || (typeof mt.ticketId === 'object' ? mt.ticketId?.name : '') || '수강권',
                                            pause: mt.pause ?? null,
                                            status: mt.status,
                                            expiryDate: mt.expiryDate ?? null,
                                            isShared: mt.access?.type === 'SHARED',
                                            ownerName: mt.access?.ownerName || '',
                                        })
                                    }
                                    style={{ fontSize: 12, fontWeight: 600, color: 'white', background: '#3B82F6', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                                    추가
                                </button>
                            </Row>
                        ))
                    ) : (
                        <Col style={{ gap: 8, alignItems: 'center', padding: '8px 0' }}>
                            <span style={{ fontSize: 13, color: '#EF4444' }}>사용 가능한 수강권이 없습니다</span>
                            <button onClick={() => onIssue(memberId, memberName)}
                                style={{ fontSize: 13, fontWeight: 600, color: 'white', background: '#F59E0B', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer' }}>
                                수강권 발급하고 예약
                            </button>
                        </Col>
                    )}
                </Col>
            )}
        </Col>
    );
};

export default GroupSessionDetailPopover;
